import { ref, Ref, watch } from 'vue'

import { Incident } from '@collector/sportsapi-client'
import { Confirmation } from '@collector/sportsapi-client-legacy'
import {
  useIncidents,
  useParticipants,
} from '@desktop/views/Relation/composables'

function getParticipantIdToIncidentsTbdMap(
  incidents: Incident[],
): Record<number, Incident[]> {
  return incidents.reduce(
    (acc, incident) => {
      const participantId = incident.participant_team_id

      if (
        incident.confirmation === Confirmation.ToBeDone &&
        participantId !== null
      ) {
        if (!acc[participantId]) {
          acc[participantId] = []
        }

        acc[participantId].push(incident)
      }

      return acc
    },
    {} as Record<number, Incident[]>,
  )
}

export function useIncidentsToConfirm(): {
  homeIncidentsTbd: Ref<Incident[]>
  awayIncidentsTbd: Ref<Incident[]>
} {
  const { incidents, incidentsUpdate } = useIncidents()
  const { homeParticipant, awayParticipant } = useParticipants()

  const participantIdToIncidentsTbdMap = getParticipantIdToIncidentsTbdMap(
    incidents.value,
  )

  const homeIncidentsTbd = ref(
    homeParticipant.value
      ? (participantIdToIncidentsTbdMap[homeParticipant.value.id] ?? [])
      : [],
  )

  const awayIncidentsTbd = ref(
    awayParticipant.value
      ? (participantIdToIncidentsTbdMap[awayParticipant.value.id] ?? [])
      : [],
  )

  watch(incidentsUpdate, (newVal) => {
    const homeId = homeParticipant.value?.id
    const awayId = awayParticipant.value?.id

    for (const incidentUpdate of newVal) {
      if (
        incidentUpdate.data.action === 'insert' &&
        incidentUpdate.data.confirmation === Confirmation.ToBeDone
      ) {
        if (incidentUpdate.data.participant_team_id === homeId) {
          homeIncidentsTbd.value.push(incidentUpdate.data)
        } else if (incidentUpdate.data.participant_team_id === awayId) {
          awayIncidentsTbd.value.push(incidentUpdate.data)
        }
      } else if (incidentUpdate.data.action === 'update') {
        const incidentId = incidentUpdate.data.id

        if (
          [Confirmation.Confirmed, Confirmation.Cancelled].includes(
            incidentUpdate.data.confirmation,
          )
        ) {
          if (incidentUpdate.data.participant_team_id === homeId) {
            const index = homeIncidentsTbd.value.findLastIndex(
              (incident) => incident.id === incidentId,
            )

            if (index !== -1) {
              homeIncidentsTbd.value.splice(index, 1)
            }
          } else if (incidentUpdate.data.participant_team_id === awayId) {
            const index = awayIncidentsTbd.value.findLastIndex(
              (incident) => incident.id === incidentId,
            )

            if (index !== -1) {
              awayIncidentsTbd.value.splice(index, 1)
            }
          }
        } else {
          const fresh = incidents.value.find(
            (incident) => incident.id === incidentId,
          )
          if (!fresh) continue

          for (const list of [homeIncidentsTbd, awayIncidentsTbd]) {
            const index = list.value.findLastIndex(
              (incident) => incident.id === incidentId,
            )
            if (index !== -1) {
              list.value[index] = fresh
            }
          }
        }
      }
    }
  })

  return {
    homeIncidentsTbd,
    awayIncidentsTbd,
  }
}
